import { Box, Chip } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { Text } from 'components/Text';
import { getTrans, getTransWithParams } from 'modules/i18n';
import { useProgressCalculation } from '../hooks/useProgressCalculation';
import { getProgressPosition, PROGRESS_STEPS, StepMeta } from '../stepMeta';
import { SavedIndicator } from './SavedIndicator';

/**
 * Orientation for the current wizard step: where you are, what this step is called,
 * whether it is required, and whether your work is saved.
 *
 * Replaces a bare `3/7` counter on a continuous bar. Each of the 7 progress steps gets its
 * own segment — done, current, or ahead — so position is legible without counting, which is
 * the pattern every multi-step flow we benchmarked uses.
 *
 * Rendered inside the wizard (from `StepContentSwitch`) rather than in a page header,
 * because the guest page mounts no header at all — this way both entry points get it.
 */
export const StepHeader = ({ step }: { step: StepMeta }) => {
  const { sections } = useProgressCalculation();
  const position = getProgressPosition(step.key);

  // Navigation-only steps (intro / overview / summary) are not numbered and carry no
  // segments — they aren't part of the 7.
  if (!position) {
    return null;
  }

  const completedKeys = new Set(
    sections.filter(section => section.status === 'complete').map(s => s.key)
  );

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
        {PROGRESS_STEPS.map((progressStep, index) => {
          const isCurrent = progressStep.key === step.key;
          const isDone = completedKeys.has(progressStep.key);
          const segmentColor = (theme: Theme) => {
            if (isDone) return theme.palette.success.main;
            if (isCurrent) return theme.palette.primary.main;
            return theme.palette.divider;
          };
          return (
            <Box
              key={progressStep.key}
              data-testid={`step-segment-${index + 1}`}
              aria-hidden
              sx={theme => ({
                flex: 1,
                minWidth: 0,
                height: 5,
                borderRadius: 3,
                backgroundColor: segmentColor(theme),
                outline: isCurrent
                  ? `2px solid ${theme.palette.primary.light}`
                  : 'none',
                outlineOffset: 1,
                transition: 'background-color 0.3s ease-in-out'
              })}
            />
          );
        })}
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1
        }}
      >
        <Text
          variant="caption"
          data-testid="step-header-position"
          sx={{ minWidth: 0 }}
        >
          {getTransWithParams('cvBuilderPage.stepHeader.position', {
            current: position,
            total: PROGRESS_STEPS.length
          })}
          {' · '}
          {getTrans(step.labelKey)}
        </Text>

        <Chip
          size="small"
          label={
            step.requiresData
              ? getTrans('cvBuilderPage.stepHeader.required')
              : getTrans('cvBuilderPage.stepHeader.optional')
          }
          data-testid="step-header-badge"
          sx={theme => ({
            flex: 'none',
            height: 20,
            fontSize: '0.6875rem',
            backgroundColor: step.requiresData
              ? theme.palette.primary.lighter
              : theme.palette.action.hover,
            color: step.requiresData
              ? theme.palette.primary.dark
              : theme.palette.text.secondary
          })}
        />
      </Box>

      {/* Optional sections are the ones talents skip and operators miss — say who reads
          them rather than making the step look disposable. */}
      {!step.requiresData && (
        <Text variant="caption" sx={{ display: 'block', mt: 0.5 }}>
          {getTrans('cvBuilderPage.stepHeader.optionalHint')}
        </Text>
      )}

      <SavedIndicator />
    </Box>
  );
};
